import JSZip from "jszip";
import { logger } from "@/lib/logger";

export interface DocxCustomProperty {
  name: string;
  value: string;
  type: "string" | "number" | "boolean" | "date";
}

export interface DocxMetadataExtractionResult {
  fileName: string;
  customProperties: DocxCustomProperty[];
  hasCustomProperties: boolean;
}

/**
 * Extracts custom properties from a DOCX file
 * @param file - The DOCX file to extract metadata from
 * @returns Promise with extraction result
 */
export const extractDocxCustomProperties = async (
  file: File
): Promise<DocxMetadataExtractionResult> => {
  try {
    const zip = await JSZip.loadAsync(file);

    // Check if custom properties exist
    const customPropsFile = zip.file("docProps/custom.xml");
    if (!customPropsFile) {
      return {
        fileName: file.name,
        customProperties: [],
        hasCustomProperties: false,
      };
    }

    const customPropsContent = await customPropsFile.async("text");
    const parser = new DOMParser();
    const doc = parser.parseFromString(customPropsContent, "application/xml");

    const properties: DocxCustomProperty[] = [];
    const propertyNodes = doc.querySelectorAll("cp\\:property, property");
    propertyNodes.forEach((node) => {
      const name = node.getAttribute("name") || node.getAttribute("cp:name");
      // Get the type from the first child element's localName
      const firstChild = node.firstElementChild;
      const type = firstChild ? firstChild.localName : "string";

      const value = node.textContent || "";

      if (name && value) {
        // Map Office property types to our types
        let mappedType: "string" | "number" | "boolean" | "date" = "string";

        switch (type.toLowerCase()) {
          case "i4":
          case "int":
          case "r8":
          case "double":
            mappedType = "number";
            break;
          case "bool":
            mappedType = "boolean";
            break;
          case "date":
          case "datetime":
          case "filetime":
            mappedType = "date";
            break;
          default:
            mappedType = "string";
        }

        properties.push({
          name,
          value,
          type: mappedType,
        });
      }
    });

    const result = {
      fileName: file.name,
      customProperties: properties,
      hasCustomProperties: properties.length > 0,
    };

    return result;
  } catch (error) {
    logger.warn("Failed to extract DOCX custom properties", {
      operation: "extractDocxCustomProperties",
      error,
      fileSize: file.size,
    });
    return {
      fileName: file.name,
      customProperties: [],
      hasCustomProperties: false,
    };
  }
};

/**
 * Extracts custom properties from multiple DOCX files
 * @param files - Array of DOCX files
 * @returns Promise with extraction results for all files
 */
export const extractMultipleDocxCustomProperties = async (
  files: File[]
): Promise<DocxMetadataExtractionResult[]> => {
  const docxFiles = files.filter((file) =>
    file.name.toLowerCase().endsWith(".docx")
  );

  const results = await Promise.all(
    docxFiles.map((file) => extractDocxCustomProperties(file))
  );

  return results;
};
